import { DisplayCity } from '@sales-app/api/DisplayCity/DisplayCity'
import { GeoLocation, Property_API_Response } from '@sales-app/api/GeoLocation/GeoLocation'
import { transformToPropertyAPIQuery } from '@sales-app/context/workspace/homeFilters.util'
import { useWorkspaceContext } from '@sales-app/context/workspace/workspace.hooks'
import { keepPreviousData } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useMemo } from 'react'

import { useAPI } from '@/hooks'

const noResults = { data: { properties: [] } }

export function useRecommendedProperties() {
  const [{ tabs, activeTabId, activeTabType }] = useWorkspaceContext()
  const activeTab = tabs.find((t) => t.bookablePersonId === activeTabId && t.bookablePersonType === activeTabType)
  const filters = activeTab?.potentialBookingFilters || { listView: true }

  const { neLatitude, neLongitude, swLatitude, swLongitude, displayCity: displayCityObj, reservationType } = filters
  const displayCity = displayCityObj?.slug
  const isGeoLocationQuery = !!swLatitude || !!swLongitude || !!neLatitude || !!neLongitude

  const propertyApi = useMemo(() => {
    return isGeoLocationQuery || !displayCity
      ? GeoLocation.getGeoLocationProperties()
      : DisplayCity.getProperties(displayCity)
  }, [isGeoLocationQuery, displayCity])

  const propertyAPIQueryParams = transformToPropertyAPIQuery(filters)

  const {
    data: propertyData,
    error,
    ...restQuery
  } = useAPI<Property_API_Response, AxiosError>(propertyApi, {
    params: {
      ...propertyAPIQueryParams,
      installed: reservationType === 'ShortTerm' ? true : undefined,
      standby: reservationType === 'Standby' ? true : undefined,
    },
    retry: !isGeoLocationQuery && !!displayCity,
    enabled: !!displayCity || isGeoLocationQuery,
    placeholderData: keepPreviousData,
  })

  const data = useMemo(() => {
    const isWithinZoomBounds = error?.response?.status !== 422 || !isGeoLocationQuery
    return isWithinZoomBounds ? propertyData : noResults
  }, [propertyData, error, isGeoLocationQuery])

  return { ...restQuery, error, data }
}
